import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Download, Loader2 } from 'lucide-react';

// Inline preview for uploaded grant documents — admin and secretary both use
// this so they can SEE the document without forcing a download.
//
// Why we fetch-then-blob rather than just `<iframe src={url}>`:
//   • The download/preview URL is on the BACKEND origin, while the SPA lives
//     on the frontend origin.
//   • Loading a cross-origin authenticated URL directly inside an <iframe>
//     leaves cookie attachment up to the browser — Chrome's PDF viewer in
//     particular has been observed to drop SameSite=None credentials when
//     navigating an iframe to a third-party document, which leaves the
//     preview frame blank.
//   • The deterministic fix is to do the credentialed fetch ourselves
//     (axios already wires `withCredentials`), wrap the response in a Blob,
//     and feed the browser an `object URL` — that URL is same-origin (it's
//     `blob:https://your-frontend-domain/...`) so the embedded PDF viewer
//     loads cleanly with no extra cookie negotiation.
//
// Props:
//   • open      — boolean
//   • onClose   — () => void
//   • title     — string (filename)
//   • url       — full URL (download/preview endpoint on the backend)
//   • contentType — string  (best-effort; we also trust the response header)
export const DocumentPreviewModal = ({ open, onClose, title, url, contentType }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [effectiveType, setEffectiveType] = useState(contentType || '');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // Reset when modal closes or URL changes.
    let cancelled = false;
    let currentObjectUrl = null;
    setErrorMsg('');
    setBlobUrl(null);
    setEffectiveType(contentType || '');

    if (!open || !url) return undefined;

    setLoading(true);
    fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Server returned ${res.status}`);
        }
        const ct = res.headers.get('Content-Type') || contentType || 'application/octet-stream';
        const buf = await res.blob();
        if (cancelled) return;
        const u = URL.createObjectURL(buf);
        currentObjectUrl = u;
        setBlobUrl(u);
        setEffectiveType(ct);
      })
      .catch((err) => {
        if (!cancelled) setErrorMsg(err?.message || 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (currentObjectUrl) {
        // Free the blob — keeping it around causes memory growth as the
        // user previews many files in a session.
        try { URL.revokeObjectURL(currentObjectUrl); } catch (_) {
          // Older Safari may throw — non-fatal.
        }
      }
    };
  }, [open, url, contentType]);

  const ct = (effectiveType || '').toLowerCase();
  const isPdf = ct.includes('pdf') || /\.pdf$/i.test(title || '');
  const isImage = ct.startsWith('image/') || /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(title || '');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-4 py-3 border-b flex flex-row items-center justify-between gap-3">
          <DialogTitle className="text-sm font-mono truncate">{title}</DialogTitle>
          {(blobUrl || url) && (
            <Button asChild size="sm" variant="outline">
              <a href={blobUrl || url} download={title} target="_blank" rel="noreferrer">
                <Download className="h-4 w-4 mr-1.5" />
                Download
              </a>
            </Button>
          )}
        </DialogHeader>
        <div className="flex-1 bg-muted/30 overflow-auto">
          {loading ? (
            <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading preview…
            </div>
          ) : errorMsg ? (
            <div className="p-10 text-center space-y-3">
              <div className="text-sm text-rose-700">{errorMsg}</div>
              {url && (
                <Button asChild>
                  <a href={url} target="_blank" rel="noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Open directly
                  </a>
                </Button>
              )}
            </div>
          ) : !blobUrl ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Nothing to preview.
            </div>
          ) : isPdf ? (
            <iframe
              src={blobUrl}
              title={title}
              className="w-full h-[80vh] bg-white"
              data-testid="doc-preview-iframe"
            />
          ) : isImage ? (
            <div className="p-4 flex items-center justify-center min-h-[60vh]">
              <img
                src={blobUrl}
                alt={title}
                className="max-w-full max-h-[80vh] object-contain shadow-md rounded"
                data-testid="doc-preview-image"
              />
            </div>
          ) : (
            <div className="p-10 text-center space-y-3">
              <div className="text-sm text-muted-foreground">
                In-browser preview isn&apos;t supported for this file type.
              </div>
              <Button asChild>
                <a href={blobUrl} download={title} target="_blank" rel="noreferrer">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </a>
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewModal;
